import { getIdentityLoginUrl } from '@atlassian/pillar/runtime/env';

const CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY = 'twg-microsite.auth.redirect-count';

/**
 * Sentinel set immediately before redirecting the user to the identity
 * provider in {@link attemptClientLogin}. Survives the same-tab redirect
 * via sessionStorage and is consumed by {@link consumeLoginPendingMarker}
 * the first time `fetchUserProfile` resolves successfully after the
 * redirect, so we can distinguish a real login from a session resume
 * (e.g. a page reload with a still-valid auth cookie).
 */
const LOGIN_PENDING_STORAGE_KEY = 'twg-microsite.auth.login-pending';

/**
 * Maximum number of times the user will be redirected to the auth page before instead
 * showing a login failed error.
 */
const MAX_REDIRECT_COUNT = 1;

function getRedirectCount(): number {
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	const count = globalThis.window.sessionStorage.getItem(CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY);
	return count ? parseInt(count, 10) : 0;
}

export function hasAutomaticAuthFailed(): boolean {
	return getRedirectCount() > MAX_REDIRECT_COUNT;
}

/**
 * Pillar builds the login URL around the current address. When the session
 * expired on a route that sends signed-out visitors elsewhere, the current
 * address is no longer where the visitor was, so the captured one takes its
 * place: directly in `continue`, or inside the local dev proxy's `redirect` hop.
 */
const withReturnTo = (loginUrl: string, returnTo: string | undefined): string => {
	if (returnTo === undefined || returnTo === '') {
		return loginUrl;
	}

	const url = new URL(loginUrl);
	const continueTo = url.searchParams.get('continue');

	if (continueTo !== null) {
		try {
			const continueUrl = new URL(continueTo);
			if (continueUrl.searchParams.has('redirect')) {
				continueUrl.searchParams.set('redirect', returnTo);
				url.searchParams.set('continue', continueUrl.toString());
				return url.toString();
			}
		} catch {
			// Not an absolute URL, so there is no proxy hop to preserve.
		}
	}

	url.searchParams.set('continue', returnTo);
	return url.toString();
};

export function attemptClientLogin(returnTo?: string): void {
	const newRedirectCount = getRedirectCount() + 1;
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	globalThis.window.sessionStorage.setItem(
		CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY,
		newRedirectCount.toString(),
	);
	// Mark that we have just initiated a login redirect. The success path
	// of `fetchUserProfile` reads + removes this to fire `track.user.loggedIn`
	// at most once per actual login (not on every reload with a valid
	// session cookie).
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	globalThis.window.sessionStorage.setItem(LOGIN_PENDING_STORAGE_KEY, '1');

	globalThis.window.location.replace(withReturnTo(getIdentityLoginUrl(), returnTo));
}

/**
 * Returns `true` exactly once, the first time it is called after the
 * sentinel was set by {@link attemptClientLogin}. Subsequent calls (and
 * page loads where no login was initiated) return `false`. Safe in SSR /
 * non-browser contexts (returns `false` if `sessionStorage` is unavailable).
 */
export function consumeLoginPendingMarker(): boolean {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const pending = globalThis.window.sessionStorage.getItem(LOGIN_PENDING_STORAGE_KEY) === '1';
		if (pending) {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			globalThis.window.sessionStorage.removeItem(LOGIN_PENDING_STORAGE_KEY);
		}
		return pending;
	} catch {
		return false;
	}
}

export function cleanupClientAuthFlow(): void {
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	globalThis.window.sessionStorage.removeItem(CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY);
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	globalThis.window.sessionStorage.removeItem(LOGIN_PENDING_STORAGE_KEY);
}
